// ============================================
// useVoiceCommand — Web Speech API hook
// Handles mic access, speech recognition,
// transcript capture, and AI intent parsing.
// ============================================

import { useState, useRef, useCallback, useEffect } from 'react';
import { useData } from '../data/DataContext';
import { useLocation } from 'react-router';
import { parseVoiceCommand, type VoiceCommandResult, type VoiceCommandContext } from '../data/api';

// Extend window for webkit speech recognition
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

type SpeechRecognitionInstance = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
};

export type VoiceState = 'idle' | 'listening' | 'processing' | 'confirming' | 'executing' | 'error' | 'success';

export interface UseVoiceCommandReturn {
  state: VoiceState;
  transcript: string;
  interimTranscript: string;
  result: VoiceCommandResult | null;
  error: string | null;
  isSupported: boolean;
  startListening: () => void;
  stopListening: () => void;
  confirm: () => void;
  cancel: () => void;
  // Extra context for focus mode
  setFocusModeContext: (inFocusMode: boolean, currentTask?: string) => void;
}

export function useVoiceCommand(): UseVoiceCommandReturn {
  const { projects, deliverables, teamMembers } = useData();
  const location = useLocation();

  const [state, setState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [result, setResult] = useState<VoiceCommandResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const transcriptRef = useRef('');
  const interimRef = useRef('');
  // Confirmed final text accumulated across the whole listening session.
  // A session may span several SpeechRecognition instances (the engine ends
  // itself after a silence; we restart to keep the mic open), and each
  // instance starts with an empty results list — so finalized text has to be
  // carried here rather than read back off the current instance.
  const finalizedRef = useRef('');
  // True once the user has explicitly stopped/cancelled. Guards onend from
  // restarting recognition after a deliberate stop.
  const manualStopRef = useRef(false);
  const focusModeRef = useRef(false);
  const focusTaskRef = useRef<string | undefined>();

  // Check browser support
  const SpeechRecognition = typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : null;
  const isSupported = !!SpeechRecognition;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      manualStopRef.current = true; // stop onend from restarting after unmount
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch {}
      }
    };
  }, []);

  const setFocusModeContext = useCallback((inFocusMode: boolean, currentTask?: string) => {
    focusModeRef.current = inFocusMode;
    focusTaskRef.current = currentTask;
  }, []);

  const buildContext = useCallback((): VoiceCommandContext => ({
    projectNames: projects.map(p => p.name),
    deliverableNames: deliverables.map(d => d.name),
    teamMemberNames: teamMembers.map(t => t.name),
    currentPage: location.pathname,
    inFocusMode: focusModeRef.current || location.pathname.includes('/focus'),
    currentFocusTask: focusTaskRef.current,
  }), [projects, deliverables, teamMembers, location.pathname]);

  // Create and start one recognition instance. Held in a ref so onend can
  // recursively restart the session (push-to-talk keeps the mic open across
  // the engine's own silence timeouts). Reassigned each render so it always
  // closes over the latest setters/refs; those are all stable, so this is cheap.
  const beginRecognitionRef = useRef<() => void>(() => {});
  beginRecognitionRef.current = () => {
    const recognition = new SpeechRecognition() as SpeechRecognitionInstance;
    // continuous: keep listening through natural pauses instead of ending on
    // the first finalized phrase — the user decides when to stop (release
    // Space / tap the mic), so we must capture the whole statement.
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setState('listening');
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // Rebuild from the entire results list (not just resultIndex) so that
      // multiple finalized segments accumulate instead of the newest one
      // overwriting the earlier text.
      let instanceFinal = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          instanceFinal += res[0].transcript;
        } else {
          interim += res[0].transcript;
        }
      }
      const fullFinal = (finalizedRef.current + instanceFinal).trim();
      transcriptRef.current = fullFinal;
      interimRef.current = interim;
      setTranscript(fullFinal);
      setInterimTranscript(interim);
    };

    recognition.onerror = (event: any) => {
      const errType = event.error;
      if (errType === 'aborted') {
        // User cancelled — not an error.
        return;
      }
      if (errType === 'no-speech') {
        // Silence timeout. While the user is still holding, this is just a
        // pause — let onend restart the session. Only surface it after a
        // deliberate stop (handled in stopListening's empty-transcript path).
        if (!manualStopRef.current) return;
        setError("I didn't hear anything. Try again?");
        setState('error');
        return;
      }
      // Permission / fatal errors: stop for good, don't restart-loop.
      manualStopRef.current = true;
      if (errType === 'not-allowed' || errType === 'service-not-allowed') {
        setError('Microphone access denied. Check your browser settings.');
      } else {
        setError(`Speech error: ${errType}`);
      }
      setState('error');
    };

    recognition.onend = () => {
      // Fold this instance's finalized text into the session accumulator so a
      // restart doesn't lose it (the next instance starts with empty results).
      finalizedRef.current = transcriptRef.current ? `${transcriptRef.current} ` : '';
      // If the user hasn't stopped yet, the engine ended on its own (a pause):
      // restart to keep capturing until they release.
      if (!manualStopRef.current) {
        // Drop the trailing non-final interim from the instance that just
        // ended — it was never finalized, and leaving it on screen reads as a
        // freeze until the next onresult lands on the new instance.
        interimRef.current = '';
        setInterimTranscript('');
        try {
          beginRecognitionRef.current();
          return;
        } catch {
          setError('Failed to keep listening. Please try again.');
          setState('error');
        }
      }
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      setError('Speech recognition not supported in this browser');
      setState('error');
      return;
    }

    // Reset state and session accumulators.
    setTranscript('');
    setInterimTranscript('');
    setResult(null);
    setError(null);
    transcriptRef.current = '';
    interimRef.current = '';
    finalizedRef.current = '';
    manualStopRef.current = false;

    try {
      beginRecognitionRef.current();
    } catch (err) {
      setError('Failed to start speech recognition');
      setState('error');
    }
  }, [SpeechRecognition]);

  const stopListening = useCallback(async () => {
    // Mark the stop as deliberate BEFORE stopping so the resulting onend
    // tears down instead of restarting the session.
    manualStopRef.current = true;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }

    // Wait for the engine to finalize any trailing words (a final onresult
    // then onend) before we read the transcript.
    await new Promise(r => setTimeout(r, 250));

    const finalText = (transcriptRef.current || interimRef.current).trim();
    if (!finalText) {
      setError("I didn't catch that. Try again?");
      setState('error');
      return;
    }

    setTranscript(finalText);
    setState('processing');

    try {
      const res = await parseVoiceCommand(finalText, buildContext());
      setResult(res);
      if (res.action) {
        setState('confirming');
      } else {
        setError(res.message || "I didn't understand that. Try rephrasing?");
        setState('error');
      }
    } catch (err) {
      console.error('Voice parse error:', err);
      setError('Failed to process command. Please try again.');
      setState('error');
    }
  }, [buildContext]);

  const confirm = useCallback(() => {
    setState('executing');
    // The parent component handles actual execution
    // and calls cancel() when done
  }, []);

  const cancel = useCallback(() => {
    // Deliberate teardown — block any pending onend from restarting.
    manualStopRef.current = true;
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }
    setState('idle');
    setTranscript('');
    setInterimTranscript('');
    transcriptRef.current = '';
    interimRef.current = '';
    finalizedRef.current = '';
    setResult(null);
    setError(null);
  }, []);

  return {
    state,
    transcript,
    interimTranscript,
    result,
    error,
    isSupported,
    startListening,
    stopListening,
    confirm,
    cancel,
    setFocusModeContext,
  };
}